import { useState, useEffect, useRef } from 'react';
import { AuthService } from '@/Entities/AuthService';

/**
 * Hook unificado para gerenciamento de sessão
 * Coordena validação, renovação e expiração de tokens
 */
export function useSessionManager() {
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [expiredReason, setExpiredReason] = useState('');
  const timerRef = useRef(null);
  const isValidatingRef = useRef(false);

  // 1️⃣ Validar sessão ao iniciar aplicação (UMA VEZ)
  useEffect(() => {
    validateInitialSession();
  }, []);

  // 2️⃣ Escutar evento de expiração do interceptor
  useEffect(() => {
    const handleSessionExpired = (event) => {
      stopTimer();
      
      const reason = event.detail?.reason || 'token_refresh_failed';
      setExpiredReason(reason);
      setShowExpiredModal(true);
      
      // Armazenar motivo para mostrar na tela de login
      sessionStorage.setItem('logoutReason', reason);
    };

    window.addEventListener('session_expired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('session_expired', handleSessionExpired);
    };
  }, []);

  // 3️⃣ Iniciar timer de renovação proativa
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      startProactiveRenewal();
    }

    return () => {
      stopTimer();
    };
  }, []);

  /**
   * Valida sessão ao iniciar aplicação
   */
  const validateInitialSession = async () => {
    // Não validar se não estiver autenticado
    if (!AuthService.isAuthenticated()) {
      return;
    }

    // Evitar validações simultâneas
    if (isValidatingRef.current) {
      return;
    }

    isValidatingRef.current = true;

    try {
      const result = await AuthService.checkTokenExpiration();

      if (result.isExpired) {
        handleSessionExpired('already_expired');
      } else if (result.isExpiringSoon) {
        try {
          await AuthService.refreshSession();
        } catch (error) {
          console.error('Erro ao renovar sessão:', error);
          handleSessionExpired('validation_renewal_failed');
        }
      }
    } catch (error) {
      console.error('Erro ao validar sessão:', error);
      // Se erro 401, o interceptor vai lidar
      // Se outro erro, pode ser problema de rede - não deslogar
      if (error.response?.status === 401) {
        handleSessionExpired('validation_error');
      }
    } finally {
      isValidatingRef.current = false;
    }
  };

  /**
   * Inicia timer de renovação proativa
   * Verifica a cada 5 minutos e renova se faltar menos de 30 minutos
   */
  const startProactiveRenewal = () => {
    // Limpar timer anterior se existir
    stopTimer();

    timerRef.current = setInterval(async () => {
      // Não verificar se não estiver autenticado
      if (!AuthService.isAuthenticated()) {
        stopTimer();
        return;
      }

      try {
        const result = await AuthService.checkTokenExpiration();

        if (result.isExpired) {
          handleSessionExpired('expired_during_use');
        } else if (result.expiresIn < 1800) { // Menos de 30 minutos
          try {
            await AuthService.refreshSession();
          } catch (error) {
            console.error('Erro ao renovar sessão:', error);
            handleSessionExpired('auto_renewal_failed');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar expiração:', error);
        // Se erro 401, o interceptor vai lidar
        if (error.response?.status === 401) {
          handleSessionExpired('check_expiration_error');
        }
      }
    }, 5 * 60 * 1000); // 5 minutos
  };

  /**
   * Para o timer de renovação
   */
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * Trata expiração de sessão
   */
  const handleSessionExpired = (reason) => {
    stopTimer();
    setExpiredReason(reason);
    setShowExpiredModal(true);
    sessionStorage.setItem('logoutReason', reason);
  };

  /**
   * Executa logout limpo
   */
  const handleLogout = async () => {
    stopTimer();
    
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('❌ Erro ao fazer logout:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('user');
      localStorage.removeItem('sessionStartTime');
      
      // ✅ SOLUÇÃO: replace() limpa histórico (melhor que href)
      // Permite que botão "Voltar" no login funcione corretamente
      window.location.replace('/login');
    }
  };

  return {
    showExpiredModal,
    expiredReason,
    handleLogout,
    setShowExpiredModal
  };
}
